import { useCallback } from 'react';

import { useCurrentUser, type IsNewRowEntityFn } from '../../../../EntitiesGrid';
import { editableResolved, editableStaff } from '../../../../EntitiesGrid/state/setters/hooks/utils';
import type { ColDefEditableFnParams } from '../../../../EntitiesGrid/state/setters/types';

type Params = {
  isPreparedEntity: IsNewRowEntityFn;
  nonEditableFields: string[];
  nonEditableOnPreparedFields: string[];
  permissions: {
    canUpdateResolvedTasks: boolean;
    canUpdateAllTask: boolean;
    canUpdateTask: boolean;
    canCreateComment: boolean;
  };
};

export const useEditable = <E extends object>({
  isPreparedEntity,
  nonEditableFields,
  nonEditableOnPreparedFields,
  permissions,
}: Params) => {
  const { currentUserId } = useCurrentUser();
  const { canUpdateAllTask, canUpdateResolvedTasks, canUpdateTask, canCreateComment } = permissions;

  const editableFn = useCallback(
    ({ data, colDef }: ColDefEditableFnParams<E>) => {
      const field = colDef?.field;
      if (!field) {
        return false;
      }
      const nonEditableAlways = nonEditableFields.includes(field);
      const resolvedCheck =
        field === 'comments' ? canCreateComment : editableResolved({ data, canUpdateResolvedTasks });
      const staffCheck = editableStaff({
        data,
        canUpdateAllTask,
        canUpdateTask,
        currentUserId,
        field,
      });

      if (nonEditableAlways) {
        return false;
      }

      if (isPreparedEntity(data)) {
        return !nonEditableOnPreparedFields.includes(field);
      }

      return resolvedCheck && staffCheck;
    },
    [
      nonEditableFields,
      canCreateComment,
      canUpdateResolvedTasks,
      canUpdateAllTask,
      canUpdateTask,
      currentUserId,
      isPreparedEntity,
      nonEditableOnPreparedFields,
    ],
  );

  return {
    editable: editableFn,
  };
};
